import { useQuery } from "@tanstack/react-query";
import { Botao, Cartao, CartaoTopo, Selo, Tabela, Vazio, Avatar, type Coluna } from "@notes/ui";
import { ArrowLeft, Users, Building2, Mic } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { trpc, mensagemDeErro } from "../lib/api.js";

type Membro = {
  usuarioId: string; nome: string; email: string;
  papel: string; recorder: boolean; ativo: boolean;
};

const PAPEL: Record<string, string> = {
  admin: "Administra", gestor: "Gerencia a equipe", consultor: "Vende", viewer: "Só lê",
};

/**
 * AGÊNCIA E EQUIPE. Quem participa, com que papel, e quem pode gravar.
 *
 * A tela não CRIA acesso: o produto não tem autocadastro, e criar gente por
 * aqui abriria um caminho paralelo ao convite. Ela mostra o que existe, que é
 * o que o administrador precisa para conferir.
 */
export function ConfigEquipe() {
  const agencia = useQuery({ queryKey: ["agencia", "minha"], queryFn: () => trpc.agencia.minha.query() });
  const equipe = useQuery({ queryKey: ["agencia", "equipe"], queryFn: () => trpc.agencia.equipe.query() });

  if (equipe.isLoading || agencia.isLoading) return <Pagina titulo="Agência e equipe"><Carregando tipo="tabela" /></Pagina>;
  if (equipe.isError) {
    return <Pagina titulo="Agência e equipe"><Erro mensagem={mensagemDeErro(equipe.error)} aoTentarDeNovo={() => void equipe.refetch()} /></Pagina>;
  }

  const org = agencia.data?.organizacao;
  const membros = (equipe.data?.membros ?? []) as Membro[];
  const gravam = membros.filter((m) => m.recorder && m.ativo).length;

  const colunas: Array<Coluna<Membro>> = [
    {
      chave: "pessoa", cabecalho: "Pessoa", ordenarPor: (m) => m.nome,
      celula: (m) => (
        <span className="enotes-tb-pessoa">
          <Avatar nome={m.nome} tamanho={26} />
          <span className="enotes-tb-titulo">
            <span className="enotes-tb-nome">{m.nome}</span>
            <span className="carimbo">{m.email}</span>
          </span>
        </span>
      ),
    },
    {
      chave: "papel", cabecalho: "Papel", largura: "12rem", ordenarPor: (m) => m.papel,
      celula: (m) => <Selo tom={m.papel === "admin" ? "marca" : "neutro"}>{PAPEL[m.papel] ?? m.papel}</Selo>,
    },
    {
      chave: "grava", cabecalho: "Grava conversa", largura: "10rem", ordenarPor: (m) => (m.recorder ? 1 : 0),
      celula: (m) => m.recorder
        ? <Selo tom="bom"><Mic size={11} aria-hidden />pode gravar</Selo>
        : <span className="carimbo">não grava</span>,
    },
    {
      chave: "ativo", cabecalho: "Situação", largura: "8rem", ordenarPor: (m) => (m.ativo ? 1 : 0),
      celula: (m) => m.ativo ? <Selo tom="bom">ativo</Selo> : <Selo tom="critico">desativado</Selo>,
    },
  ];

  return (
    <Pagina
      titulo="Agência e equipe"
      descricao="Quem participa desta agência, com que papel, e quem pode gravar conversa."
      acao={<Link to="/configuracoes"><Botao tamanho="sm" variante="fantasma"><ArrowLeft size={14} />Configurações</Botao></Link>}
    >
      {org && (
        <Secao ordem={0}>
          <Cartao>
            <CartaoTopo icone={<Building2 size={15} />} titulo={org.nome}
              descricao={`Fuso ${org.timezone} · plano ${org.plano} · mídia guardada por ${org.retencaoMidiaDias} dias`} />
            <dl className="enotes-dados">
              <div><dt>Consentimento</dt><dd>{ROTULO_CONSENTIMENTO[org.politicaConsentimento] ?? org.politicaConsentimento}</dd></div>
              <div><dt>Pessoas na agência</dt><dd className="num">{membros.filter((m) => m.ativo).length}</dd></div>
              <div><dt>Podem gravar</dt><dd className="num">{gravam}</dd></div>
            </dl>
          </Cartao>
        </Secao>
      )}

      <Secao titulo="Pessoas" ordem={1}>
        <Tabela colunas={colunas} linhas={membros} chave={(m) => m.usuarioId}
          vazio={<Vazio titulo="Ninguém além de você" descricao="O acesso é criado pela agência; peça a quem administra para incluir a pessoa." icone={<Users size={26} />} />} />
      </Secao>
    </Pagina>
  );
}

const ROTULO_CONSENTIMENTO: Record<string, string> = {
  anunciar: "a gravação é anunciada na reunião",
  anunciar_e_registrar: "anunciada e registrada em cada conversa",
  exigir_confirmacao: "exige confirmação de quem está na sala",
};
